import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";

// ============================================================================
// GAZEAL ELITE HEADER — fixed, translucent, liquid glass on scroll
// Ported and rebranded from Gazeal Advance. Gold accent (#FFDE59 → #FF914D)
// ============================================================================

const EliteHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: "Home", href: "#" },
    { name: "Secure", href: "#secure" },
    { name: "Advance", href: "#advance" },
    { name: "FAQ", href: "#faq" }];


  const handleNav = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const targetId = href === "#" ? null : href.slice(1);
    const scroll = () => {
      if (!targetId) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      const el = document.getElementById(targetId);
      if (!el) return;
      const headerOffset = 90;
      const y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    };
    if (location.pathname === "/" || location.pathname === "/fc" || location.pathname === "/fine-and-country") {
      scroll();
    } else {
      navigate("/");
      setTimeout(scroll, 100);
    }
  };

  const handleUnlock = () => {
    setIsMobileMenuOpen(false);
    const el = document.getElementById("access-section");
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled ? "py-3" : "py-4"}`
        }
        data-testid="elite-header">

        <div
          className={`max-w-[1400px] mx-auto transition-all duration-700 ${
          isScrolled ? "px-4" : "px-6 lg:px-12"}`
          }>

          <div
            className={`relative transition-all duration-700 ease-out ${
            isScrolled ?
            "bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] rounded-full px-6 py-2" :
            "bg-transparent px-2 py-2"}`
            }
            style={
            isScrolled ?
            {
              background:
              "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 50%, rgba(0,0,0,0.1) 100%)",
              boxShadow:
              "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.1)"
            } :
            {}
            }>

            {/* Inner highlights when scrolled */}
            {isScrolled &&
            <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-white/[0.08] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>
            }

            <div className="relative flex items-center justify-between h-14">
              {/* Logo */}
              <a
                href="#"
                onClick={(e) => handleNav(e, "#")}
                className="flex items-center z-10"
                data-testid="elite-logo">
                <img
                  src="/gazeal-elite-logo.png"
                  alt="Gazeal Elite"
                  className={`transition-all duration-500 ${
                  isScrolled ? "h-8 md:h-9" : "h-10 md:h-12"} w-auto`
                  } />

              </a>

              {/* Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-8">
                {navLinks.map((link) =>
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNav(e, link.href)}
                  className="text-white/60 hover:text-white transition-all duration-300 text-[14px] font-medium relative group font-manrope"
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}>

                    {link.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-gradient-to-r from-[#FFDE59] to-[#FF914D] transition-all duration-300 group-hover:w-full" />
                  </a>
                )}
              </nav>

              {/* CTA */}
              <div className="hidden lg:flex items-center">
                <button
                  type="button"
                  onClick={handleUnlock}
                  className="text-[14px] px-5 py-2.5 flex items-center gap-2 font-semibold rounded-full bg-gradient-to-r from-[#FFDE59] to-[#FF914D] text-black hover:opacity-90 transition-opacity font-manrope shadow-[0_4px_15px_rgba(255,145,77,0.25)]"
                  data-testid="nav-cta-button">

                  Unlock Access
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Mobile toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden relative z-50 p-2 text-white hover:text-[#FF914D] transition-colors"
                data-testid="mobile-menu-button">

                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-all duration-500 ${
        isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`
        }>

        <div
          className={`absolute inset-0 transition-all duration-700 ${
          isMobileMenuOpen ? "backdrop-blur-2xl" : "backdrop-blur-none"}`
          }
          style={{
            background: isMobileMenuOpen ?
            "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(10,10,10,0.95) 30%, rgba(0,0,0,0.98) 100%)" :
            "transparent"
          }}
          onClick={() => setIsMobileMenuOpen(false)} />


        <div
          className={`absolute top-24 left-4 right-4 transition-all duration-500 ease-out ${
          isMobileMenuOpen ?
          "translate-y-0 opacity-100 scale-100" :
          "-translate-y-8 opacity-0 scale-95"}`
          }>

          <div
            className="relative overflow-hidden rounded-3xl"
            style={{
              background:
              "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 50%, rgba(0,0,0,0.2) 100%)",
              backdropFilter: "blur(40px) saturate(180%)",
              WebkitBackdropFilter: "blur(40px) saturate(180%)",
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow:
              "0 25px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.2)"
            }}>

            {/* Accents */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#FF914D]/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#FFDE59]/10 rounded-full blur-3xl" />

            <div className="relative p-6">
              <div className="space-y-2 mb-6">
                {navLinks.map((link, i) =>
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNav(e, link.href)}
                  className="block text-white/80 hover:text-white text-xl font-medium py-4 px-4 rounded-2xl transition-all duration-300 hover:bg-white/[0.05] border border-transparent hover:border-white/[0.08] font-manrope"
                  style={{
                    transitionDelay: `${i * 50}ms`,
                    transform: isMobileMenuOpen ? "translateX(0)" : "translateX(-20px)",
                    opacity: isMobileMenuOpen ? 1 : 0
                  }}>

                    {link.name}
                  </a>
                )}
              </div>

              <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent mb-6" />

              <button
                type="button"
                onClick={handleUnlock}
                className="w-full text-center py-4 text-lg font-semibold rounded-2xl transition-all duration-300 bg-gradient-to-r from-[#FFDE59] to-[#FF914D] text-black hover:opacity-90 shadow-[0_8px_25px_rgba(255,145,77,0.3)] font-manrope"
                data-testid="mobile-nav-cta">

                Unlock Access
              </button>
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default EliteHeader;
